/* eslint-disable @typescript-eslint/no-unused-vars */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { isNil } from 'ramda';
import {
  FILTER_ALL_VALUE,
  mapOfferLocationsEnums,
  mapOfferRoomsEnums,
  mapOfferStatusEnums,
} from 'shared/constants/offersHistory';
import { selectHouseId } from 'shared/selectors/houseInfo';
import { selectOffersHistory } from 'shared/selectors/offersHistory';
import { fetchHouseOfferHistory } from 'shared/services/fetchHouseOfferHistory';
import {
  saveDefaultFilters,
  setOffersHistoryLoadMore,
  setRoomsFilter,
  setStatusFilter,
  setTypeFilter,
} from 'shared/slices/offersHistory';
import { ETypeFilterValues, IOffersHistoryListingData } from 'shared/types/offersHistory';
import { IThunkApi } from 'shared/types/redux';

interface IThunkApiExtended extends IThunkApi {
  rejectValue: void;
}

export interface IGetOffersHistoryResult {
  data: IOffersHistoryListingData;
  isLoadMore?: boolean;
}

export const getOffersHistory = createAsyncThunk<IGetOffersHistoryResult, boolean | undefined, IThunkApiExtended>(
  'offersHistory/getOffersHistory',
  async (isLoadMore, { getState, dispatch, extra: context, rejectWithValue }) => {
    const state = getState();
    const houseId = selectHouseId(state);
    const { resultsOnPage, page, statusFilterActiveValue, roomsFilterActiveValue, typeFilterActiveValue } =
      selectOffersHistory(state);

    if (isLoadMore) {
      dispatch(setOffersHistoryLoadMore(true));
    }

    const result = await fetchHouseOfferHistory(context, {
      parameters: {
        houseId,
        offerStatus: filterOutDefaultVal(statusFilterActiveValue),
        roomsCount: filterOutDefaultVal(roomsFilterActiveValue),
        resultsOnPage,
        page,
        isNearby: isNil(typeFilterActiveValue) ? undefined : typeFilterActiveValue === ETypeFilterValues.NearbyHouses,
      },
    });

    if (!result) {
      return rejectWithValue();
    }

    /** Предустановка активных значений из ответа API, если таковые есть, или дефолтов */
    if (result.activeFiltersValues) {
      const {
        locations: _locations,
        statusCounts: _statusCounts,
        roomCounts: _roomCounts,
      } = result.activeFiltersValues;

      const location = mapOfferLocationsEnums[_locations];
      const statusCounts = _statusCounts ? mapOfferStatusEnums[_statusCounts] : FILTER_ALL_VALUE;
      const roomCounts = _roomCounts ? mapOfferRoomsEnums[_roomCounts] : FILTER_ALL_VALUE;

      dispatch(setTypeFilter(location));
      dispatch(setStatusFilter(statusCounts));
      dispatch(setRoomsFilter(roomCounts));
      dispatch(saveDefaultFilters({ location, roomCounts, statusCounts }));
    }

    return { data: result, isLoadMore };
  },
);

/** Отфильтровывает значение 'all' из фильтра, т.к в api оно не должно отправляться */
function filterOutDefaultVal<V extends string | undefined>(value: V | typeof FILTER_ALL_VALUE): V | undefined {
  return value === FILTER_ALL_VALUE ? undefined : value;
}
